import React from 'react'
import { Button, CircularProgress, Box, Typography, styled } from '@mui/material'

/**
 * Componente Button estilizado con variantes personalizadas y tamaños.
 * @private
 */
const StyledButton = styled(Button)(({ theme, version, size }) => ({
  borderRadius: 6,
  textTransform: 'none',
  fontFamily: 'FSEmeric-Medium',
  ...(version === 'delete' && {
    backgroundColor: '#dd422a',
    '&:hover': {
      backgroundColor: '#c83a25',
    },
  }),
  ...(size === 'large' && {
    padding: '4px 22px',
    fontSize: '0.875rem',
  }),
  ...(size === 'medium' && {
    padding: '6px 16px',
    fontSize: '0.875rem',
  }),
  ...(size === 'small' && {
    padding: '4px 8px',
    fontSize: '0.8125rem',
  }),
}))

const truncateSx = {
  display: 'inline-block',
  width: '12em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'pre-line',
}

const multilineSx = {
  whiteSpace: 'pre-line',
}

/**
 * @typedef {object} ThemeButtonProps
 * @property {'text' | 'outlined' | 'contained'} [variant='contained'] - La variante visual del botón.
 * @property {'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'default'} [color='primary'] - El color del botón.
 * @property {'small' | 'medium' | 'large'} [size='large'] - El tamaño del botón.
 * @property {React.ReactNode} children - El contenido del botón (texto, iconos, etc.).
 * @property {boolean} [loading=false] - Si es `true`, muestra un spinner de carga.
 * @property {boolean} [loadingShowText=false] - Si es `true` y `loading` es `true`, muestra el texto junto al spinner.
 * @property {boolean} [truncate=false] - Si es `true`, trunca el texto extra con ellipsis.
 * @property {object} [sx] - Estilos adicionales de Material-UI.
 * @property {'delete'} [version] - Variante especial que aplica estilos de botón de eliminación (rojo).
 * @property {React.ReactNode} [extra] - Contenido adicional a mostrar debajo del botón (como texto de ayuda).
 * @property {boolean} [disabled] - Si es `true`, el botón estará deshabilitado.
 * @property {function(React.MouseEvent): void} [onClick] - La función callback que se ejecuta al hacer clic.
 */

/**
 * Un componente de botón temático personalizado con estados de carga y variantes especiales.
 * 
 * Extiende el componente `Button` de Material-UI con estilos personalizados, soporte para
 * estados de carga con spinner, variante especial de eliminación, y contenido extra opcional
 * debajo del botón. Los tamaños y colores son configurables y siguen el sistema de diseño
 * de la aplicación.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [loading, setLoading] = useState(false);
 * 
 *   const handleSubmit = async () => {
 *     setLoading(true);
 *     await submitForm();
 *     setLoading(false);
 *   };
 * 
 *   return (
 *     <ThemeButton
 *       variant="contained"
 *       color="primary"
 *       size="large"
 *       loading={loading}
 *       loadingShowText
 *       onClick={handleSubmit}
 *     >
 *       Guardar
 *     </ThemeButton>
 *   );
 * };
 * 
 * @example
 * // Botón de eliminación
 * <ThemeButton
 *   version="delete"
 *   onClick={handleDelete}
 * >
 *   Eliminar
 * </ThemeButton>
 * 
 * @example
 * // Botón con texto extra
 * <ThemeButton
 *   color="secondary"
 *   extra="Esta acción no se puede deshacer"
 * >
 *   Confirmar
 * </ThemeButton>
 * 
 * @param {ThemeButtonProps} props - Las props del componente ThemeButton.
 */
export const ThemeButton = ({
  variant = 'contained',
  color = 'primary',
  size = 'large',
  children,
  loading = false,
  loadingShowText = false,
  truncate = false,
  sx,
  ...props
}) => {
  const finalColor = color === 'default' ? 'inherit' : color

  return (
    <Box display="block">
      <StyledButton
        {...props}
        size={size}
        variant={variant}
        color={finalColor}
        disabled={props.disabled || loading}
        disableElevation
        version={props.version}
        sx={sx}
      >
        {loading ? (
          loadingShowText ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={size === 'large' ? 25 : 15} color="inherit" />
              {children}
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <CircularProgress size={size === 'large' ? 30 : 25} color="inherit" />
            </Box>
          )
        ) : (
          <Box display="flex" alignItems="center">
            {children}
          </Box>
        )}
      </StyledButton>
      {props.extra && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={truncate ? truncateSx : multilineSx}
          >
            {props.extra}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
